import { useContext, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "../../../../utils/axiosInstance";
import { AuthContext } from "../../../../context/AuthContext";
import ConfirmModal from "../../../../components/Modals/ConfirmModal";
import BackButton from "../../../../components/Button/BackButton";
import { toast } from "../../../../components/Toast/toast";
import { btn } from "../../../../components/Button/buttonStyles";
import TaxForm from "./TaxForm";
import TaxList from "./TaxList";

const errorMessage = (err, fallback) =>
  err.response?.data?.error || err.response?.data?.message || fallback;

const byName = (a, b) => a.name.localeCompare(b.name);

const TaxesPage = () => {
  const { user } = useContext(AuthContext);
  const canEdit = user?.roleSlug === "administrator";

  const [taxes, setTaxes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [taxToDelete, setTaxToDelete] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();

  const action = canEdit ? searchParams.get("action") : null;
  const editId = searchParams.get("id");

  useEffect(() => {
    api
      .get("/taxes")
      .then((res) => setTaxes(res.data.data))
      .catch((err) => setError(errorMessage(err, "Failed to fetch taxes.")))
      .finally(() => setLoading(false));
  }, []);

  const setAction = (nextAction, id) => {
    const next = new URLSearchParams(searchParams);
    if (nextAction) next.set("action", nextAction);
    else next.delete("action");
    if (id !== undefined && id !== null) next.set("id", String(id));
    else next.delete("id");
    setSearchParams(next, { replace: true });
  };

  const handleSave = async (payload) => {
    try {
      if (action === "edit") {
        const res = await api.put(`/taxes/${editId}`, payload);
        setTaxes((prev) =>
          prev.map((t) => (String(t.id) === String(editId) ? res.data.data : t)).sort(byName)
        );
      } else {
        const res = await api.post("/taxes", payload);
        setTaxes((prev) => [...prev, res.data.data].sort(byName));
      }
      toast.success(action === "edit" ? "Tax updated." : "Tax created.");
      setAction(null);
      return null;
    } catch (err) {
      const message = errorMessage(err, "Could not save the tax.");
      if (err.response?.status !== 409) return { message };
      return { field: /type/i.test(message) ? "type" : "name", message };
    }
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/taxes/${taxToDelete}`);
      setTaxes((prev) => prev.filter((t) => t.id !== taxToDelete));
      toast.success("Tax deleted.");
    } catch (err) {
      toast.error(errorMessage(err, "Failed to delete the tax."));
    } finally {
      setTaxToDelete(null);
    }
  };

  if (loading) return <div className="text-center mt-10 text-gray-500">Loading...</div>;

  const editingTax =
    action === "edit" ? taxes.find((t) => String(t.id) === String(editId)) : null;
  const inForm = action === "add" || Boolean(editingTax);

  return (
    <>
      <div className="md:w-2/3">
        <div className="flex justify-between items-center gap-3 mb-6">
          <div className="flex items-center gap-3">
            <BackButton onClick={inForm ? () => setAction(null) : undefined} />
            <h1 className="text-[22px] font-semibold tracking-tight text-gray-900">Taxes</h1>
          </div>
          {canEdit && !inForm && (
            <button onClick={() => setAction("add")} className={btn("primary", "sm")}>
              Add Tax
            </button>
          )}
        </div>

        {error && <p className="text-danger-600 text-sm mb-3">{error}</p>}

        {inForm ? (
          <TaxForm
            key={editingTax?.id ?? "new"}
            taxToEdit={editingTax}
            onSave={handleSave}
            onCancel={() => setAction(null)}
          />
        ) : (
          <TaxList
            taxes={taxes}
            canEdit={canEdit}
            onEdit={(tax) => setAction("edit", tax.id)}
            onDelete={(id) => setTaxToDelete(id)}
          />
        )}
      </div>

      {taxToDelete && (
        <ConfirmModal
          title="Confirm Deletion"
          message="Are you sure you want to delete this tax? It will no longer be available to apply, and any client or vendor default taxes using it will be removed."
          onCancel={() => setTaxToDelete(null)}
          onConfirm={handleDelete}
          confirmText="Delete"
        />
      )}
    </>
  );
};

export default TaxesPage;